import React, { useEffect } from 'react';
import { Grid, Paper, IconButton, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DialogTitle from '@mui/material/DialogTitle';

import SettingsIcon from '@mui/icons-material/Settings';
import KPISettingsDialog from './KPISettings';

import { queryTableAssetData, findMinMaxBlocks, productivePlantKPITextMap, transformToHierarchical } from './KPIUtils'

import { useDataSetters, useDataState } from '../../../../DataContext';

import { StyledCard, StyledTypographyH5, StyledTypographyBody2, StyledTypographyLevel } from './KPIStyles'

import { getJsonFromS3, saveJsonToS3 } from './KPIS3';

// KPI Cards for percent productive and non-productive plants
const ProductivityKPICards = () => {

    // App state management; see DataContext.js
     const {
        clickedDashboardTableCell,
        plantAssetTable,
        levelKPISelected,
        idKPISelected,
        productivePlantKPI,
        nonProductivePlantKPI,
        mostProductiveUnitKPI,
        leastProductiveUnitKPI,
        plantingEfficiencyKPI,
        pctWeakCanopy,
        pctWeakFruit,
        pctWeakVines,
        costPerVineKPI,
        revenuePerVineKPI,
        yieldPerVineKPI,
        kpiUserInputs,
        kpiSettingsTable,
        plantEffectiveCapacityUtilization,
    } = useDataState();

    const {
        setLevelKPISelected,
        setIdKPISelected,
        setProductivePlantKPI,
        setNonProductivePlantKPI,
        setMostProductiveUnitKPI,
        setLeastProductiveUnitKPI,
        setPlantingEfficiencyKPI,
        setPctWeakCanopy,
        setPctWeakFruit,
        setPctWeakVines,
        setCostPerVineKPI,
        setRevenuePerVineKPI,
        setYieldPerVineKPI,
        setPlantEffectiveCapacityUtilization,
    } = useDataSetters();

    // Update percent productive and non-productive KPI cards
    useEffect(() => {

        // Initializing KPI card at "All Vineyards" depth, or
        // Update KPI card based on depth of user click
        // const hierarchicalData = transformToHierarchical(kpiSettingsTable);
        let results;
        const hierarchicalData = transformToHierarchical(kpiSettingsTable)

        let revenueSelectedLevel = [];
        let costSelectedLevel = [];
        let yieldSelectedLevel = [];
        let weakCanopyThreshSelectedLevel = [];
        let weakFruitThreshSelectedLevel = [];
        if (hierarchicalData.length > 0) {
            revenueSelectedLevel = hierarchicalData[0].revenue;
            costSelectedLevel = hierarchicalData[0].cost;
            yieldSelectedLevel = hierarchicalData[0].yield;
            weakCanopyThreshSelectedLevel = hierarchicalData[0].canopy_threshold;
            weakFruitThreshSelectedLevel = hierarchicalData[0].yield_threshold;
        }

        if (clickedDashboardTableCell.column.Header == 'no cell selected') {
            
            results = queryTableAssetData(
                plantAssetTable, 
                "All Vineyards",
                0
            );  

            if (results) {
                // Check if results.subRows is truthy and is an array before proceeding
                if (results.subRows && Array.isArray(results.subRows)) {
                    const { maxBlock, minBlock } = findMinMaxBlocks(results.subRows, 'number_mature_vines', ['total_vines'], 0);
                    setMostProductiveUnitKPI(maxBlock);
                    setLeastProductiveUnitKPI(minBlock);
                } else {
                    // Handle the case where subRows is null or not an array
                    setMostProductiveUnitKPI('--');
                    setLeastProductiveUnitKPI('--');
                }
            }
            
            setLevelKPISelected('vineyards');
            setIdKPISelected('Vineyards');

        } else {
            
            results = queryTableAssetData(
                plantAssetTable, 
                clickedDashboardTableCell.row.values.id,
                clickedDashboardTableCell.row.depth
            );

            console.log('plantAssetTable', plantAssetTable);

            setIdKPISelected(results.id);

            if (clickedDashboardTableCell.row.depth == 0) {
                setLevelKPISelected('vineyards');
                const { maxBlock, minBlock } = findMinMaxBlocks(results, 'number_mature_vines', ['total_vines'], 0);
                setMostProductiveUnitKPI(maxBlock);
                setLeastProductiveUnitKPI(minBlock);
                revenueSelectedLevel = hierarchicalData[0].revenue;
                costSelectedLevel = hierarchicalData[0].cost;
                yieldSelectedLevel = hierarchicalData[0].yield;
            }

            if (clickedDashboardTableCell.row.depth == 1) {
                setLevelKPISelected('blocks');
                // Check if results.subRows is truthy and is an array before proceeding
                if (results.subRows && Array.isArray(results.subRows)) {
                    const { maxBlock, minBlock } = findMinMaxBlocks(results.subRows, 'number_mature_vines', ['total_vines'], 1);
                    setMostProductiveUnitKPI(maxBlock);
                    setLeastProductiveUnitKPI(minBlock);
                    revenueSelectedLevel = hierarchicalData[0].revenue;
                    costSelectedLevel = hierarchicalData[0].cost;
                    yieldSelectedLevel = hierarchicalData[0].yield;
                } else {
                    // Handle the case where subRows is null or not an array
                    setMostProductiveUnitKPI('--');
                    setLeastProductiveUnitKPI('--');
                }
            }

            if (clickedDashboardTableCell.row.depth == 2) {
                setLevelKPISelected('block')
                setMostProductiveUnitKPI('--');
                setLeastProductiveUnitKPI('--');

                const blockIdToMatch = clickedDashboardTableCell.row.values.id;
                const matchingItem = hierarchicalData[0].children.find(item => item.block_id === blockIdToMatch);
                revenueSelectedLevel = matchingItem.revenue;
                costSelectedLevel = matchingItem.cost;
                yieldSelectedLevel = matchingItem.yield;
            }     
        }

        // Set Productive and Non-Productive plant percentage KPIs
        if (results && results.total_vines != 0) {
            const total_vines_minus_nodata = results.number_mature_vines + results.number_young_vines + results.number_replanted_vines + results.number_rootstocks + results.number_missing_vines + results.number_weak_vines;
            const total_vines_minus_missing_and_nodata = results.number_mature_vines + results.number_young_vines + results.number_replanted_vines + results.number_rootstocks + results.number_weak_vines;
            const total_vines_minus_missing = results.number_mature_vines + results.number_young_vines + results.number_replanted_vines + results.number_rootstocks + results.number_nodata_vines + results.number_weak_vines;
            const pctWeakVines = (100 * (results.number_weak_vines / total_vines_minus_missing_and_nodata)).toFixed(0);
            const pctWeakCanopy = (100 * (results.number_weak_canopies / total_vines_minus_missing_and_nodata)).toFixed(0);
            const pctWeakFruit = (100 * (results.number_weak_fruits / total_vines_minus_missing_and_nodata)).toFixed(0);
            const pctProductive = (100 * ((results.number_mature_vines + results.number_weak_vines) / total_vines_minus_nodata)).toFixed(0);
            const pctNonProductive = 100 - pctProductive;
            const plantingEfficiency = 100 * (total_vines_minus_missing_and_nodata / total_vines_minus_nodata).toFixed(2);
            const costPerVine = (costSelectedLevel / total_vines_minus_missing).toFixed(2);
            const revenuePerVine = (revenueSelectedLevel / total_vines_minus_missing).toFixed(2);
            const yieldPerVine = (2000 * yieldSelectedLevel / total_vines_minus_missing).toFixed(1);
            const plantEffectiveCapacityUtil = (100 * (results.number_weak_vines / (results.number_weak_vines + results.number_mature_vines))).toFixed(0);
            setProductivePlantKPI(isNaN(pctProductive) ? '--' : pctProductive);
            setNonProductivePlantKPI(isNaN(pctNonProductive) ? '--' : pctNonProductive);
            setPlantingEfficiencyKPI(isNaN(plantingEfficiency) ? '--': plantingEfficiency);
            setCostPerVineKPI(isNaN(costPerVine) ? '--' : costPerVine);
            setRevenuePerVineKPI(isNaN(revenuePerVine) ? '--' : revenuePerVine);
            setYieldPerVineKPI(isNaN(yieldPerVine) ? '--' : yieldPerVine);
            setPlantEffectiveCapacityUtilization(isNaN(plantEffectiveCapacityUtil) ? '--' : plantEffectiveCapacityUtil);
            setPctWeakVines(isNaN(pctWeakVines) ? '--' : pctWeakVines);
            setPctWeakCanopy(isNaN(pctWeakCanopy) ? '--' : pctWeakCanopy);
            setPctWeakFruit(isNaN(pctWeakFruit) ? '--' : pctWeakFruit);
            
        } else {
            setProductivePlantKPI(null);
            setNonProductivePlantKPI(null);
            setMostProductiveUnitKPI(null);
            setLeastProductiveUnitKPI(null);
            setPlantingEfficiencyKPI(null);
            setCostPerVineKPI(null);
            setRevenuePerVineKPI(null);
            setYieldPerVineKPI(null);
            setPlantEffectiveCapacityUtilization(null);
            setPctWeakVines(null);
            setPctWeakCanopy(null);
            setPctWeakFruit(null);
        }

    }, [plantAssetTable, clickedDashboardTableCell, kpiUserInputs, kpiSettingsTable]);

    return (
        <>
        <Grid item xs={12} md={4} style={{paddingLeft: 10, paddingTop: 10, margin: 0}}>
            <Paper elevation={2} style={{ backgroundColor: '#F8F8F8', padding: 10 }}>
            <Typography variant="button" component="div" style={{ padding: '8px 0', alignContent: 'center' }}>
                Asset Utilization
            </Typography>
            <Grid container spacing={1} wrap="wrap">
                <Grid item> 
                    <StyledCard elevation={2}>
                        <Tooltip title={"% strong + weak mature vines divided by total spaces, excluding no data"} placement="top">
                            <IconButton 
                                aria-label="info" 
                                size="small"
                                style={{ position: 'absolute', top: 4, right: 4 }} 
                            >
                                <InfoOutlinedIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                        <StyledTypographyH5 variant="h5">
                            { productivePlantKPI === null ? '--' : productivePlantKPI }
                            { productivePlantKPI === null ? '' : '%' }
                        </StyledTypographyH5>
                        <StyledTypographyBody2 variant="body2">
                            Producing Plants
                        </StyledTypographyBody2>
                        <StyledTypographyLevel variant="body2">
                            {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                        </StyledTypographyLevel>
                    </StyledCard>
                </Grid>
                <Grid item> 
                    <StyledCard elevation={2}>
                        <Tooltip title={"% young, rootstock, replant, and missing divided by total spaces, excluding no data"} placement="top">
                            <IconButton 
                                aria-label="info" 
                                size="small"
                                style={{ position: 'absolute', top: 4, right: 4 }} 
                            >
                                <InfoOutlinedIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                        <StyledTypographyH5 variant="h5">
                            { nonProductivePlantKPI === null ? '--' : nonProductivePlantKPI }
                            { nonProductivePlantKPI === null ? '' : '%' }
                        </StyledTypographyH5>
                        <StyledTypographyBody2 variant="body2">
                            Non-Prod. Plants
                        </StyledTypographyBody2>
                        <StyledTypographyLevel variant="body2">
                            {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                        </StyledTypographyLevel>
                    </StyledCard>
                </Grid>
                <Grid item> 
                    <StyledCard elevation={2}>
                        <Tooltip title={"% of spaces without missing vines, excluding no data"} placement="top">
                            <IconButton 
                                aria-label="info" 
                                size="small"
                                style={{ position: 'absolute', top: 4, right: 4 }} 
                            >
                                <InfoOutlinedIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                        <StyledTypographyH5 variant="h5">
                            { plantingEfficiencyKPI === null ? '--' : plantingEfficiencyKPI }
                            { plantingEfficiencyKPI === null ? '' : '%' }
                        </StyledTypographyH5>
                        <StyledTypographyBody2 variant="body2">
                            Planting Efficiency
                        </StyledTypographyBody2>
                        <StyledTypographyLevel variant="body2">
                            {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                        </StyledTypographyLevel>
                    </StyledCard>
                </Grid>
                <Grid item> 
                    <StyledCard elevation={2}>
                        <Tooltip title={"Vineyard with highest % of Producing Plants"} placement="top">
                            <IconButton 
                                aria-label="info" 
                                size="small"
                                style={{ position: 'absolute', top: 4, right: 4 }} 
                            >
                                <InfoOutlinedIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                        <StyledTypographyH5 variant="h5">
                            { mostProductiveUnitKPI === null ? '--' : mostProductiveUnitKPI }
                        </StyledTypographyH5>
                        <StyledTypographyBody2 variant="body2">
                            Most Productive
                        </StyledTypographyBody2>
                        <StyledTypographyLevel variant="body2">
                            {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                        </StyledTypographyLevel>
                    </StyledCard>
                    </Grid>
                <Grid item> 
                    <StyledCard elevation={2}>
                        <Tooltip title={"Vineyard with lowest % of Producing Plants"} placement="top">
                            <IconButton 
                                aria-label="info" 
                                size="small"
                                style={{ position: 'absolute', top: 4, right: 4 }} 
                            >
                                <InfoOutlinedIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                        <StyledTypographyH5 variant="h5">
                            {leastProductiveUnitKPI === null ? '--' : leastProductiveUnitKPI }
                        </StyledTypographyH5>
                        <StyledTypographyBody2 variant="body2">
                            Least Productive
                        </StyledTypographyBody2>
                        <StyledTypographyLevel variant="body2">
                            {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                        </StyledTypographyLevel>
                    </StyledCard>
                </Grid>

            </Grid>
            </Paper>
            </Grid>
            <Grid item xs={12} md={4} style={{paddingTop: 10, paddingLeft: 15, margin: 0}}>
            <Paper elevation={2} style={{ backgroundColor: '#f7f7f7', padding: 10 }}>
                <Typography variant="button" component="div" style={{ padding: '8px 0', alignContent: 'center' }}>
                    Yield and Quality
                </Typography>
                <Grid container spacing={1} wrap="wrap">
                    
                    <Grid item> 
                        <StyledCard elevation={2}>
                            <Tooltip title={"% of vines below weak vine threshold, excluding no data"} placement="top">
                                <IconButton 
                                    aria-label="info" 
                                    size="small"
                                    style={{ position: 'absolute', top: 4, right: 4 }} 
                                >
                                    <InfoOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            <StyledTypographyH5 variant="h5">
                                { pctWeakVines ? pctWeakVines + '%' : '--' }
                            </StyledTypographyH5>
                            <StyledTypographyBody2 variant="body2">
                                % Weak Vines
                            </StyledTypographyBody2>
                            <StyledTypographyLevel variant="body2">
                                {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                            </StyledTypographyLevel>
                        </StyledCard>
                    </Grid>
                    <Grid item> 
                        <StyledCard elevation={2}>
                            <Tooltip title={"% of vines below weak canopy threshold, excluding no data"} placement="top">
                                <IconButton 
                                    aria-label="info" 
                                    size="small"
                                    style={{ position: 'absolute', top: 4, right: 4 }} 
                                >
                                    <InfoOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            <StyledTypographyH5 variant="h5">
                                { pctWeakCanopy ? pctWeakCanopy + '%' : '--' }
                            </StyledTypographyH5>
                            <StyledTypographyBody2 variant="body2">
                                % Weak Canopy
                            </StyledTypographyBody2>
                            <StyledTypographyLevel variant="body2">
                                {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                            </StyledTypographyLevel>
                        </StyledCard>
                    </Grid>
                    <Grid item> 
                        <StyledCard elevation={2}>
                            <Tooltip title={"% of vines below weak fruit threshold, excluding no data"} placement="top">
                                <IconButton 
                                    aria-label="info" 
                                    size="small"
                                    style={{ position: 'absolute', top: 4, right: 4 }} 
                                >
                                    <InfoOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            <StyledTypographyH5 variant="h5">
                                { pctWeakFruit ? pctWeakFruit + '%' : '--' }
                            </StyledTypographyH5>
                            <StyledTypographyBody2 variant="body2">
                                % Weak Fruit
                            </StyledTypographyBody2>
                            <StyledTypographyLevel variant="body2">
                                {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                            </StyledTypographyLevel>
                        </StyledCard>
                    </Grid>
                    <Grid item> 
                        <StyledCard elevation={2}>
                            <Tooltip title={"Yield divided by total number of vines, excluding missing vines"} placement="top">
                                <IconButton 
                                    aria-label="info" 
                                    size="small"
                                    style={{ position: 'absolute', top: 4, right: 4 }} 
                                >
                                    <InfoOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            <StyledTypographyH5 variant="h5">
                                { yieldPerVineKPI === null ? '--' : yieldPerVineKPI + ' lbs' }
                                { yieldPerVineKPI === null ? '' : '' }
                            </StyledTypographyH5>
                            <StyledTypographyBody2 variant="body2">
                                Yield per Vine
                            </StyledTypographyBody2>
                            <StyledTypographyLevel variant="body2">
                                {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                            </StyledTypographyLevel>
                        </StyledCard>
                    </Grid>
                    {/* <Grid item> 
                        <StyledCard elevation={2}>
                            <Tooltip title={"% weak vines divided by total producing vines"} placement="top">
                                <IconButton 
                                    aria-label="info" 
                                    size="small"
                                    style={{ position: 'absolute', top: 4, right: 4 }} 
                                >
                                    <InfoOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            <StyledTypographyH5 variant='h5'>
                                { plantEffectiveCapacityUtilization ? plantEffectiveCapacityUtilization + '%' : '--' }
                            </StyledTypographyH5>
                            <StyledTypographyBody2 variant='body2'>
                                Plant Improvement Ratio
                            </StyledTypographyBody2>
                            <StyledTypographyLevel variant="body2">
                                {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                            </StyledTypographyLevel>
                        </StyledCard>
                    </Grid> */}
                </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12} md={4} style={{paddingRight: 10, paddingTop: 10, paddingLeft: 15, margin: 0}}>
            <Paper elevation={2} style={{ backgroundColor: '#f7f7f7', padding: 10 }}>
                <Typography variant="button" component="div" style={{ padding: '8px 0', alignContent: 'center' }}>
                    Financial Outcomes
                </Typography>
                <Grid container spacing={1} wrap="wrap">
                    <Grid item> 
                        <StyledCard elevation={2}>
                            <Tooltip title={"Revenue divided by total number of vines, excluding missing vines"} placement="top">
                                <IconButton 
                                    aria-label="info" 
                                    size="small"
                                    style={{ position: 'absolute', top: 4, right: 4 }} 
                                >
                                    <InfoOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            <StyledTypographyH5 variant="h5">
                                { revenuePerVineKPI === null ? '--' : '$' + revenuePerVineKPI }
                                { revenuePerVineKPI === null ? '' : '' }
                            </StyledTypographyH5>
                            <StyledTypographyBody2 variant="body2">
                                Revenue per Vine
                            </StyledTypographyBody2>
                            <StyledTypographyLevel variant="body2">
                                {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                            </StyledTypographyLevel>
                        </StyledCard>
                    </Grid>
                    <Grid item> 
                        <StyledCard elevation={2}>
                            <Tooltip title={"Cost divided by total number of vines, excluding missing vines"} placement="top">
                                <IconButton 
                                    aria-label="info" 
                                    size="small"
                                    style={{ position: 'absolute', top: 4, right: 4 }} 
                                >
                                    <InfoOutlinedIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                            <StyledTypographyH5 variant="h5">
                                { costPerVineKPI === null ? '--' : '$' + costPerVineKPI }
                                { costPerVineKPI === null ? '' : '' }
                            </StyledTypographyH5>
                            <StyledTypographyBody2 variant="body2">
                                Cost per Vine
                            </StyledTypographyBody2>
                            <StyledTypographyLevel variant="body2">
                                {[productivePlantKPITextMap[levelKPISelected], levelKPISelected].join(' ') || ''}
                            </StyledTypographyLevel>
                        </StyledCard>
                    </Grid>
                </Grid>
                </Paper>
            </Grid>
        </>
    );
};


const HealthKPICards = () => {

    const {
        plantEffectiveCapacityUtilization,
    } = useDataState();

    return (
        <>
            {/* <Grid item> 
                <StyledCard elevation={2}>
                    <StyledTypographyH5 variant='h5'>
                        --
                    </StyledTypographyH5>
                    <StyledTypographyBody2 variant='body2'>
                        Canopy Volume Index
                    </StyledTypographyBody2>
                </StyledCard>
            </Grid>
            <Grid item> 
                <StyledCard elevation={2}>
                    <StyledTypographyH5 variant='h5'>
                        --
                    </StyledTypographyH5>
                    <StyledTypographyBody2 variant='body2'>
                        Fruit Volume Index
                    </StyledTypographyBody2>
                </StyledCard>
            </Grid>
            <Grid item> 
                <StyledCard elevation={2}>
                    <StyledTypographyH5 variant='h5'>
                        --
                    </StyledTypographyH5>
                    <StyledTypographyBody2 variant='body2'>
                        Yield per Vine
                    </StyledTypographyBody2>
                </StyledCard>
            </Grid> */}
            
            {/* <Grid item> 
                <StyledCard elevation={2}>
                    <StyledTypographyH5 variant='h5'>
                        --
                    </StyledTypographyH5>
                    <StyledTypographyBody2 variant='body2'>
                        Projected Productivity
                    </StyledTypographyBody2>
                </StyledCard>
            </Grid>
            <Grid item> 
                <StyledCard elevation={2}>
                    <StyledTypographyH5 variant='h5'>
                        --
                    </StyledTypographyH5>
                    <StyledTypographyBody2 variant='body2'>
                        Projected Yield
                    </StyledTypographyBody2>
                </StyledCard>
            </Grid> */}
        </>
    );
};


const LastScanDateKPICard = () => {

    // App state management; see DataContext.js
    const {
        dateOptions,
    } = useDataState();

    let latestDate;
    if (dateOptions.length !== 0) {
        latestDate = dateOptions[dateOptions.length - 1];
        latestDate = latestDate.slice(0, 2) + '/' + latestDate.slice(2, 4) + '/' + latestDate.slice(4);
    }

    return (
        <>
            <Grid item> 
                <StyledCard elevation={2}>
                    <Tooltip title={"Date of last deep scan"} placement="top">
                        <IconButton 
                            aria-label="info" 
                            size="small"
                            style={{ position: 'absolute', top: 4, right: 4 }} 
                        >
                            <InfoOutlinedIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                    <StyledTypographyH5 variant='h5'>
                        {
                            latestDate ? latestDate : 'NA'
                        }
                    </StyledTypographyH5>
                    <StyledTypographyBody2 variant='body2'>
                        Last Scan
                    </StyledTypographyBody2>
                </StyledCard>
            </Grid>
        </>
    );
};

// Pull all of the KPI cards together for export to the Landing Dashboard
const KPICards = () => {

    // App state management; see DataContext.js
    const {
        kpiSettingsOpen,
        kpiSettingsTable,
        selectedBucket,
        selectedVineyard,
        selectedYear,
        fileInsightsDataCache,
        levelKPISelected,
        idKPISelected
    } = useDataState();

    const {
        setKpiSettingsTable,
        setKpiSettingsOpen,
        setFileInsightsDataCache,
    } = useDataSetters();

    const handleKpiSettingsOpen = async () => {
    
        setKpiSettingsOpen(true);
        const filepath = `${selectedBucket}/${selectedVineyard}/kit_insights/Summary_${selectedYear}/kpiSettingsTable.json`;
        try {
          const kpiData = await getJsonFromS3(filepath);
          setKpiSettingsTable(kpiData);
        } catch (error) {
          // Handle the error, possibly updating UI to notify the user
          console.error("Error fetching KPI settings data from S3:", error);
        }
    }

    const handleKpiSettingsClose = async () => {
        setKpiSettingsOpen(false);
    }

    const handleKpiSettingsSave = async () => {
    
        const filepath = `${selectedBucket}/${selectedVineyard}/kit_insights/Summary_${selectedYear}/kpiSettingsTable.json`;
        try {
          await saveJsonToS3(filepath, kpiSettingsTable);
          console.log('KPI settings saved to S3');
        } catch (error) {
          // Handle the error, possibly updating UI to notify the user
          console.error("Error saving KPI settings data to S3:", error);
        }
        setKpiSettingsOpen(false);
    }

    return (
        <Paper style={{ position: 'relative', marginBottom: '5px', marginLeft: '20px', marginTop: '80px', padding: '15px'}}>
            <DialogTitle id="settings-dialog-title">Key Performance Indicators (KPIs)</DialogTitle>
            <Typography variant="button" style={{ marginLeft: '30px' }}> 
                Viewing KPIs for <strong>{
                    levelKPISelected === "vineyards" ? 
                    idKPISelected : 
                    levelKPISelected === "blocks" ?
                    [levelKPISelected, "in", idKPISelected].join(' ') :
                    [levelKPISelected, idKPISelected].join(' ')
                }</strong>
            </Typography>
            <Grid container spacing={1} wrap="nowrap" style={{ marginTop: '10px'}}>
                <IconButton 
                    color="primary" 
                    aria-label="kpi-settings" 
                    style={{ position: 'absolute', top: '10px', right: '10px' }}
                    onClick={handleKpiSettingsOpen}
                >
                    <SettingsIcon />
                </IconButton>
                <Grid item xs={12}>
                    <Grid container spacing={1} wrap="wrap">
                        <ProductivityKPICards/>
                        {/* <LastScanDateKPICard/> */}
                    </Grid>
                </Grid>
            </Grid>
            <KPISettingsDialog open={kpiSettingsOpen} onClose={handleKpiSettingsClose} onSave={handleKpiSettingsSave} />
        </Paper>
    )
};

export default KPICards;